import React, { useState } from "react";
import ThemeModal from "./ThemeModal";
import SettingsModal from "./SettingsModal";
import { NavLink } from "react-router-dom";
import { useUserRole } from "../../hooks/useAuth";
import { UploadCloud } from "lucide-react";
import packageJson from "../../../package.json";
import { authStorage } from "../../utils/authStorage";
import {
  HomeIcon,
  CubeIcon,
  BeakerIcon,
  ClipboardDocumentListIcon,
  Squares2X2Icon,
  Cog6ToothIcon,
  PresentationChartBarIcon,
  DocumentTextIcon,
  UserIcon,
} from "@heroicons/react/24/outline";


// -------------------- Role-based Menu Definition --------------------
const menuByRole = {
  0: [ // superadmin UI
    { name: "Dashboard", to: "/superadmin/dashboard", icon: HomeIcon },
    { name: "Customer Details", to: "/superadmin/customer-details", icon: UserIcon },
    { name: "Branches", to: "/superadmin/branches", icon: Squares2X2Icon },
    { name: "Settings", to: "/settings", icon: Cog6ToothIcon },
  ],
  1: [ // admin UI
    { name: "Dashboard", to: "/admin/dashboard", icon: HomeIcon },
    { name: "User List", to: "/admin/user-list", icon: UserIcon },
    // { name: "Branches", to: "/admin/branches", icon: Squares2X2Icon },
    { name: "Products", to: "/admin/products", icon: CubeIcon },
    { name: "Product Price Logs", to: "/admin/product-price-logs", icon: ClipboardDocumentListIcon },
    { name: "Raw Materials", to: "/admin/raw-materials", icon: BeakerIcon },
    { name: "Inventory", to: "/admin/inventory", icon: Squares2X2Icon },
    // { name: "Ingredients", to: "/admin/recipes", icon: ClipboardDocumentListIcon }, // Disabled: Ingredients feature
    { name: "Analytics", to: "/admin/analytics", icon: PresentationChartBarIcon },
    { name: "Reports", to: "/admin/reports", icon: DocumentTextIcon },
    { name: "Transactions", to: "/admin/transactions", icon: PresentationChartBarIcon },
    { name: "Raw Material Logs", to: "/admin/raw-material-logs", icon: ClipboardDocumentListIcon },
    { name: "Waste Logs", to: "/admin/waste-logs", icon: ClipboardDocumentListIcon },
    { name: "Production Logs", to: "/admin/production-logs", icon: ClipboardDocumentListIcon },
    { name: "User Manual", to: "/admin/user-manual", icon: DocumentTextIcon, openInNewTab: true },
    // { name: "Calendar", to: "/admin/calendar", icon: CalendarIcon },
    // { name: "Pre Release Tracker", to: "/admin/pre-release-tracker", icon: UploadCloud }, // Disabled: Pre-release tracker feature
    { name: "Settings", to: "/settings", icon: Cog6ToothIcon },
  ],
  2: [ // POS UI (role_name-gated below)
    { name: "Sales", to: "/dashboard", icon: HomeIcon },
    { name: "Raw Material Usage", to: "/raw-material-usage", icon: BeakerIcon },
    { name: "Transactions", to: "/transactions", icon: PresentationChartBarIcon },
    { name: "Products Catalog", to: "/products", icon: CubeIcon },
    // { name: "Raw Materials Request", to: "/pre-release", icon: UploadCloud }, // Disabled: Pre-release flow
    { name: "Production", to: "/production", icon: CubeIcon },
    { name: "Production Logs", to: "/production-logs", icon: ClipboardDocumentListIcon },
    { name: "Disposal", to: "/disposal", icon: BeakerIcon },
    // { name: "Raw Materials Catalog", to: "/raw-materials-catalog", icon: CubeIcon }, // Disabled: Pre-release flow
  ],

  // 3: [ // POS UI 
  //   { name: "Sales", to: "/dashboard", icon: HomeIcon },
  //   { name: "Transactions", to: "/transactions", icon: PresentationChartBarIcon },
  //   { name: "Products Catalog", to: "/products", icon: CubeIcon },
  //   { name: "Pre release", to: "/pre-release", icon: UploadCloud },
  // ],
};

// -------------------- Mobile Header Toggle --------------------
// ...existing code...

// -------------------- Sidebar Navigation Links --------------------
function SidebarLinks({ menuItems, closeSidebar, onSettingsClick, isThemeModalOpen, isSettingsModalOpen }) {
  // Add isSettingsModalOpen to props
  return (
    <nav className="flex-1 px-4 space-y-2 overflow-y-auto" data-sidebar-links>
      {menuItems.map(({ name, to, icon: Icon, isAppearance, openInNewTab }) => {
        let navTo = to;
        return (
          <NavLink
            key={name}
            to={navTo}
            onClick={e => {
              if (isAppearance) {
                e.preventDefault();
                onSettingsClick("appearance");
                closeSidebar();
              } else if (name === "Settings") {
                e.preventDefault();
                onSettingsClick("settings");
                closeSidebar();
              } else if (openInNewTab) {
                e.preventDefault();
                const absoluteUrl = `${window.location.origin}${navTo}`;
                window.open(absoluteUrl, "_blank", "noopener,noreferrer");
                closeSidebar();
              } else {
                closeSidebar();
              }
            }}
            className={({ isActive }) => {
              let active = false;
              if (isThemeModalOpen && isAppearance) {
                active = true;
              } else if (typeof isSettingsModalOpen !== 'undefined' && isSettingsModalOpen && name === "Settings") {
                active = true;
              } else {
                active = isActive;
              }
              return `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${active
                ? "shadow-sm font-medium"
                : "hover:bg-gray-100"
                }`;
            }}
            style={({ isActive }) => {
              let active = false;
              if (isThemeModalOpen && isAppearance) {
                active = true;
              } else if (typeof isSettingsModalOpen !== 'undefined' && isSettingsModalOpen && name === "Settings") {
                active = true;
              } else {
                active = isActive;
              }
              return {
                background: active ? 'var(--color-primary)' : 'transparent',
              };
            }}
          >
            {({ isActive }) => {
              let active = false;
              if (isThemeModalOpen && isAppearance) {
                active = true;
              } else if (typeof isSettingsModalOpen !== 'undefined' && isSettingsModalOpen && name === "Settings") {
                active = true;
              } else {
                active = isActive;
              }
              return (
                <>
                  <Icon
                    className="h-5 w-5"
                    style={{ color: active ? '#ffffff' : '#6b7280' }}
                  />
                  <span
                    className="text-sm"
                    style={{ color: active ? '#ffffff' : '#1f2937' }}
                  >
                    {name}
                  </span>
                </>
              );
            }}
          </NavLink>
        );
      })}
    </nav>
  );
}

// -------------------- Main Sidebar Component --------------------
export default function Sidebar({ closeSidebar }) {
  const [isThemeModalOpen, setThemeModalOpen] = useState(false);
  const [isSettingsModalOpen, setSettingsModalOpen] = useState(false);
  const role = useUserRole();
  const storedUser = authStorage.getUser();
  const roleName = String(storedUser?.role_name || "").toLowerCase();
  const isManager = storedUser?.user_type === 2 && roleName === "manager";
  const menuItems = [...(menuByRole[role] || [])]
    .filter(item => !(isManager && (item.name === "User List" || item.name === "Branches" || item.name === "Pre Release Tracker")))
    .filter(item => {
      if (role !== 2) return true;
      if (isManager) return true;
      if (roleName === "cashier") {
        return [
          "Sales",
          "Raw Material Usage",
          "Transactions",
          "Products Catalog",
          "Production",
          "Production Logs",
          "Disposal",
        ].includes(item.name);
      }
      return false;
    });

  const handleModalOpen = (type) => {
    if (type === "appearance") {
      setThemeModalOpen(true);
    } else if (type === "settings") {
      setSettingsModalOpen(true);
    }
  };
  const handleModalClose = (type) => {
    if (type === "appearance") {
      setThemeModalOpen(false);
    } else if (type === "settings") {
      setSettingsModalOpen(false);
    }
  };

  // Example sections for SettingsModal (customize as needed)
  const settingsSections = [
    { title: "Profile", content: "Manage your profile information." },
    { title: "Notifications", content: "Set your notification preferences." },
    { title: "Security", content: "Update your password and security settings." },
  ];

  return (
    <>
      <div className={`h-full bg-white text-gray-700 flex flex-col pt-6`}>
        <SidebarLinks
          menuItems={menuItems}
          closeSidebar={closeSidebar}
          onSettingsClick={handleModalOpen}
          isThemeModalOpen={isThemeModalOpen}
          isSettingsModalOpen={isSettingsModalOpen}
        />
        <div className="px-4 py-3 text-xs text-gray-500 mt-auto border-t border-gray-200">
          Version {packageJson.version}
        </div>
      </div>
      <ThemeModal isOpen={isThemeModalOpen} onClose={() => handleModalClose("appearance")} />
      <SettingsModal isOpen={isSettingsModalOpen} onClose={() => handleModalClose("settings")} sections={settingsSections} />
    </>
  );
}
